import Vue from 'vue'
import Vuex from 'vuex'
import { decryptAES,isBase64String } from '@/utils/crypto'
import { convertServerTimeToBeijing } from '@/utils/time'
Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 用户信息
    user: null,
    authToken: localStorage.getItem('auth_token') || null,

    // 对话相关
    conversations: [],
    activeConversationId: null,

    // WebSocket连接状态
    wsConnected: false,

    // UI状态
    sidebarCollapsed: false,

    // 消息相关
    messages: {}, // 以对话ID为key的消息数组
    unreadCounts: {} // 未读消息计数
  },

  mutations: {
    // 设置用户信息
    SET_USER(state, user) {
      state.user = user
    },

    // 设置认证令牌
    SET_AUTH_TOKEN(state, token) {
      state.authToken = token
      if (token) {
        localStorage.setItem('auth_token', token)
      } else {
        localStorage.removeItem('auth_token')
      }
    },

    // 设置对话列表
    SET_CONVERSATIONS(state, conversations) {
      // 保存旧的未读计数和最后消息ID
      const oldUnreadCounts = { ...state.unreadCounts }
      const oldLastMsgIds = {}

      // 从现有对话中获取旧的lastMsgId
      state.conversations.forEach(conv => {
        if (conv.lastMsgId) {
          oldLastMsgIds[conv.id] = conv.lastMsgId
        }
      })

      // 收集旧列表中标记为注入的会话，后续在新列表中补回
      const injectedByStaff = {}
      state.conversations.forEach(admin => {
        if (!admin || !admin.sessions || admin.sessions.length === 0) return
        admin.sessions.forEach(s => {
          if (s && s.__injected) {
            const staffId = admin.staffId
            if (!injectedByStaff[staffId]) injectedByStaff[staffId] = []
            injectedByStaff[staffId].push(s)
          }
        })
      })

      state.conversations = conversations

      // 将之前注入的会话补回到对应客服下（若后端列表未包含）
      Object.keys(injectedByStaff).forEach(staffId => {
        const admin = state.conversations.find(a => a && a.staffId == staffId)
        if (!admin) return
        if (!admin.sessions) {
          Vue.set(admin, 'sessions', [])
        }
        injectedByStaff[staffId].forEach(sess => {
          const idx = admin.sessions.findIndex(s => s && s.id == sess.id)
          if (idx === -1) {
            admin.sessions.push(sess)
          }
        })
        // 重新计算客服的总未读
        admin.staffUnread = (admin.sessions || []).reduce((total, s) => total + (s && s.staffUnread ? s.staffUnread : 0), 0)
      })

      // 初始化未读消息计数（只为用户对话）
      conversations.forEach(conversation => {
        if (conversation.type === 'user') {
          const oldUnreadCount = oldUnreadCounts[conversation.id] || 0
          const oldLastMsgId = oldLastMsgIds[conversation.id]
          const currentMsgId = conversation.originalData && conversation.originalData.msgId

          // 判断是否有新消息：只有在非初始加载时（oldLastMsgId有值）且msgId不一样且不是自己发的消息
          let newUnreadCount = oldUnreadCount
          if (currentMsgId && oldLastMsgId && oldLastMsgId !== currentMsgId &&
            conversation.originalData && conversation.originalData.senderAccount !== conversation.senderAccount) {
            // 有新消息，增加未读数
            newUnreadCount = oldUnreadCount + 1
            console.log(`发现新消息，对话 ${conversation.id} 未读数从 ${oldUnreadCount} 增加到 ${newUnreadCount} (msgId: ${oldLastMsgId} -> ${currentMsgId})`)
          }

          state.unreadCounts[conversation.id] = newUnreadCount
          // 保存lastMsgId到对话中
          conversation.lastMsgId = currentMsgId
        }
      })

      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 在指定客服下插入/更新一个会话（用于搜索跳转时的临时拉取）
    UPSERT_CUSTOMER_SESSION(state, session) {
      if (!session) return
      const staffId = session.staffId || session.ownerStaffId || session.ownerId
      if (!staffId) return

      const admin = state.conversations.find(a => a && a.staffId == staffId)
      if (!admin) return

      if (!admin.sessions) {
        Vue.set(admin, 'sessions', [])
      }

      const payload = { ...session, __injected: true }
      const idx = admin.sessions.findIndex(s => s && s.id == payload.id)
      if (idx === -1) {
        admin.sessions.push(payload)
      } else {
        Vue.set(admin.sessions, idx, payload)
      }

      // 汇总未读
      admin.staffUnread = (admin.sessions || []).reduce((total, s) => total + (s && s.staffUnread ? s.staffUnread : 0), 0)

      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 添加对话
    ADD_CONVERSATION(state, conversation) {
      const index = state.conversations.findIndex(c => c.id === conversation.id)
      if (index === -1) {
        state.conversations.push(conversation)
      } else {
        state.conversations.splice(index, 1, conversation)
      }
    },

    // 移除客户会话
    REMOVE_CUSTOMER_SESSION(state, { adminId, customerId }) {
      const adminIndex = state.conversations.findIndex(admin => admin.staffId == adminId)
      if (adminIndex !== -1) {
        const admin = state.conversations[adminIndex]
        if (admin.sessions) {
          admin.sessions = admin.sessions.filter(session => session.customerId !== customerId)
          // 重新计算客服的总未读数
          admin.staffUnread = admin.sessions.reduce((total, session) => total + (session.staffUnread || 0), 0)
        }
      }

      // 清除相关未读计数
      const conversationId = `conv_${adminId}_${customerId}`
      if (state.unreadCounts[conversationId]) {
        delete state.unreadCounts[conversationId]
      }

      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 设置活动对话
    SET_ACTIVE_CONVERSATION(state, conversationId) {
      state.activeConversationId = conversationId
      // 清除该对话的未读消息数
      if (conversationId && state.unreadCounts[conversationId]) {
        state.unreadCounts[conversationId] = 0
      }

      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 设置WebSocket连接状态
    SET_WS_CONNECTED(state, connected) {
      state.wsConnected = connected
    },

    // 设置侧边栏折叠状态
    SET_SIDEBAR_COLLAPSED(state, collapsed) {
      state.sidebarCollapsed = collapsed
    },

    // 设置消息
    SET_MESSAGES(state, { conversationId, messages }) {
      console.log(`设置消息，对话ID: ${conversationId}, 消息数量: ${messages.length}`)
      Vue.set(state.messages, conversationId, messages)
    },

    // 添加消息
    ADD_MESSAGE(state, { conversationId, message }) {
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = []
      }
      state.messages[conversationId].push(message)

      // 更新对话的lastMsgId（如果消息有ID）
      const conversation = state.conversations.find(c => c.id === conversationId)
      if (conversation && message.id) {
        conversation.lastMsgId = message.id
      }

      // 如果不是当前活动对话且不是自己发送的消息，增加未读消息数
      if (state.activeConversationId !== conversationId && !message.isOwn) {
        if (!state.unreadCounts[conversationId]) {
          state.unreadCounts[conversationId] = 0
        }
        state.unreadCounts[conversationId]++
      }

      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 前置添加消息（用于加载更多历史消息）
    PREPEND_MESSAGES(state, { conversationId, messages }) {
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = []
      }

      // 将新消息添加到前面
      state.messages[conversationId].unshift(...messages)

      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 撤回消息
    RECALL_MESSAGE(state, { conversationId, messageId }) {
      if (state.messages[conversationId]) {
        const messageIndex = state.messages[conversationId].findIndex(msg => msg.id === messageId)
        if (messageIndex !== -1) {
          // 标记消息为已撤回
          state.messages[conversationId][messageIndex].isRevoked = true
          state.messages[conversationId][messageIndex].content = '消息已撤回'
        }
      }
    },

    MARK_MESSAGES_READ(state, { sessionId, readerId, conversationId }) {
      console.log("MARK_MESSAGES_READ called:", { sessionId, readerId, conversationId });
      const loginId = localStorage.getItem("login_id");

      // 🛡️ 严格控制：只有当 readerId 明确存在，且明确不是客服自己（即 readerId 不等于 loginId）时，才允许标记客服发送的消息为已读！
      if (!readerId || readerId == loginId) {
        console.log("Ignore MARK_MESSAGES_READ since readerId is missing or staff self:", readerId, loginId);
        return;
      }
      
      const targetConvIds = new Set();

      if (conversationId) {
        targetConvIds.add(conversationId);
      }
      
      if (state.activeConversation) {
        if ((sessionId && state.activeConversation.id === sessionId) || 
            (readerId && state.activeConversation.customerId === readerId)) {
          if (state.activeConversation.customId) targetConvIds.add(state.activeConversation.customId);
          if (state.activeConversationId) targetConvIds.add(state.activeConversationId);
        }
      }

      if (Array.isArray(state.conversations)) {
        state.conversations.forEach(admin => {
          if (Array.isArray(admin.sessions)) {
            admin.sessions.forEach(session => {
              if ((sessionId && session.id === sessionId) || 
                  (readerId && session.customerId === readerId)) {
                if (session.customId) targetConvIds.add(session.customId);
                targetConvIds.add(`conv_${admin.staffId}_${session.customerId}`);
              }
            });
          }
        });
      }

      Object.keys(state.messages).forEach(key => {
        if ((sessionId && (key.endsWith(`_${sessionId}`) || key.includes(`_${sessionId}`))) || 
            (readerId && (key.endsWith(`_${readerId}`) || key.includes(`_${readerId}`)))) {
          targetConvIds.add(key);
        }
      });

      console.log("Target conversation IDs to mark read:", Array.from(targetConvIds));

      targetConvIds.forEach(convId => {
        const messages = state.messages[convId];
        if (messages && Array.isArray(messages)) {
          messages.forEach(message => {
            if (message.isOwn) {
              Vue.set(message, 'isRead', true);
            }
          });
        }
      });
    },

    // 设置未读消息数
    SET_UNREAD_COUNT(state, { conversationId, count }) {
      state.unreadCounts[conversationId] = count
      // 保存到本地存储
      this.commit('SAVE_CONVERSATIONS_TO_STORAGE')
    },

    // 保存对话数据到本地存储
    SAVE_CONVERSATIONS_TO_STORAGE(state) {
      try {
        const dataToSave = {
          conversations: state.conversations,
          unreadCounts: state.unreadCounts,
          activeConversationId: state.activeConversationId,
          lastSaved: new Date().toISOString()
        }
        localStorage.setItem('chat_conversations', JSON.stringify(dataToSave))
      } catch (error) {
        console.error('保存对话数据到本地存储失败:', error)
      }
    },

    // 从本地存储加载对话数据
    LOAD_CONVERSATIONS_FROM_STORAGE(state) {
      try {
        const savedData = localStorage.getItem('chat_conversations')
        if (savedData) {
          const data = JSON.parse(savedData)
          state.conversations = data.conversations || []
          state.unreadCounts = data.unreadCounts || {}
          state.activeConversationId = data.activeConversationId || null
          console.log('从本地存储加载了对话数据:', data.lastSaved)
        }
      } catch (error) {
        console.error('从本地存储加载对话数据失败:', error)
        // 清除损坏的数据
        localStorage.removeItem('chat_conversations')
      }
    },

    // 清除所有状态
    CLEAR_STATE(state) {
      state.user = null
      state.authToken = null
      state.conversations = []
      state.activeConversationId = null
      state.wsConnected = false
      state.messages = {}
      state.unreadCounts = {}
      localStorage.removeItem('auth_token')
      localStorage.removeItem('login_id')
    }
  },

  actions: {
    // 登录
    async login({ commit }, credentials) {
      const response = await Vue.prototype.$http.post('/api/staff/login', credentials)

      if (response.data && response.data.code === 200) {
        const { token, id } =isBase64String(response.data.data) ? decryptAES(response.data.data) :response.data.data
        commit('SET_AUTH_TOKEN', token)
        localStorage.setItem('login_id', id)
        return response.data
      } else {
        throw new Error(response.data.msg || '登录失败')
      }
    },

    // 登出
    logout({ commit }) {
      commit('CLEAR_STATE')
    },

    // 加载对话列表
    async loadConversations({ commit }) {
      try {
        let sessionid = localStorage.getItem("selected_session_id");
        if(sessionid) {
          await Vue.prototype.$http.post('/api/session/read', {
            sessionId: sessionid,
          })
        }
        const response = await Vue.prototype.$http.get('/api/session/list', {})
        if (response.data && response.data.data) {
          const staffList =isBase64String(response.data.data) ?  decryptAES(response.data.data) :response.data.data
          
          // staffList
          staffList.map((item) => {
            let staffUnread = 0
            item.sessions.map(session => {
              if (session.staffUnread > 0) {
                staffUnread += session.staffUnread
              }
            })
            item.staffUnread = staffUnread
          })
          // console.log(staffList, 'staffList')
          commit('SET_CONVERSATIONS', staffList)
          return staffList
        }
      } catch (error) {
        console.error('Failed to load conversations:', error)
        throw error
      }
    },

    /**
     * 加载指定对话的消息历史记录
     * 
     * @param {Object} commit - Vuex commit 对象，用于提交状态变更
     * @param {Object} params - 包含对话信息的参数对象
     * @param {string} params.conversationId - 对话ID
     * @param {Object} params.conversation - 对话信息对象
     * @returns {Promise<Array>} 返回消息数组，每个消息包含完整的信息字段
     * @throws {Error} 当加载消息历史失败时抛出错误
     */
    // 统一的加载消息方法
    async loadMessages({ commit, state }, { conversationId, conversation, pageNum = 1, pageSize = 50, prepend = false }) {
      try {
        console.log(`${prepend ? '加载更多' : '加载'}消息历史:`, conversationId, conversation, pageNum, pageSize);

        // 使用新的历史记录接口
        const sessionId = conversation.id;

        const response = await Vue.prototype.$http.get('/api/message/staff/history', {
          params: {
            sessionId: sessionId,
            pageNum: pageNum,
            pageSize: pageSize
          }
        });

        if (response.data && response.data.code === 200) {
          const records =isBase64String(response.data.data) ? decryptAES(response.data.data).records : response.data.data.records || [];
          console.log(records,'records');
          
          // 转换消息格式 - 使用 messageId 作为前端唯一标识
          const messages = records.map(record => ({
            id: record.messageId, // string - 消息UUID作为前端唯一标识
            serverId: record.id, // number - 后端会话记录ID
            content: record.content, // string
            type: record.messageType, // number
            sender: record.senderId, // string
            receiver: record.receiverId, // string
            createTime: convertServerTimeToBeijing(record.createTime), // 转换为北京时间
            isOwn: record.senderId != conversation.customerId, // boolean
            senderName: record.senderName, // string
            senderAvatar: record.senderAvatar, // string
            receiverName: record.receiverName, // string
            receiverAvatar: record.receiverAvatar, // string
            isRead: record.readStatus === 1 || record.isRead === 1, // boolean
            isRevoked: record.isRevoked === 1 // boolean
          }));


          // createTime: "2026-01-09 14:11:18"

          // 按时间排序消息（时间升序，最新的在底部）
          messages.sort((a, b) => new Date(a.createTime) - new Date(b.createTime));

          console.log(`${prepend ? '加载更多' : '加载'}完成，消息数量:`, messages.length);
          if (messages.length > 0) {
            console.log("第一条消息:", messages[0]);
          }

          // 根据 prepend 参数决定如何处理消息
          if (prepend) {
            commit('PREPEND_MESSAGES', { conversationId, messages });
          } else {
            commit('SET_MESSAGES', { conversationId, messages });

            // 加载消息后，标记为已读（只在首次加载时，不在加载更多历史消息时）
            try {
              const response = await Vue.prototype.$http.post('/api/session/read', {
                sessionId: sessionId
              });
              if (response.data && response.data.code === 200) {
                // 更新会话状态 conversations  sessions  staffUnread = 0
                // 清除该会话的未读消息计数
                // 查找对应的客服会话，清除sessions中的customerUnread
                const adminIndex = state.conversations.findIndex(admin =>
                  admin.sessions && admin.sessions.some(session => session.id === sessionId)
                );

                if (adminIndex !== -1) {
                  const admin = state.conversations[adminIndex];
                  const sessionIndex = admin.sessions.findIndex(session => session.id === sessionId);

                  if (sessionIndex !== -1) {
                    // 清除会话的未读计数
                    admin.sessions[sessionIndex].customerUnread = 0;
                    admin.sessions[sessionIndex].staffUnread = 0;

                    // 重新计算客服的总未读数
                    admin.staffUnread = admin.sessions.reduce((total, session) =>
                      total + (session.staffUnread || 0), 0
                    );
                  }
                }
              }
              console.log(`标记会话 ${sessionId} 消息为已读`);
            } catch (error) {
              console.error('标记消息已读失败:', error);
              // 已读接口失败不影响消息加载，不抛出错误
            }
          }

          return messages;
        } else {
          throw new Error((response.data && response.data.msg) || '加载失败');
        }
      } catch (error) {
        console.error(`Failed to ${prepend ? 'load more' : 'load'} messages:`, error);
        throw error;
      }
    },

    // 兼容性方法：加载更多历史消息
    async loadMoreMessages({ dispatch }, params) {
      return dispatch('loadMessages', { ...params, prepend: true });
    },

    // 发送消息
    async sendMessage({ commit, state }, { conversation, content, type = 1 }) {
      try {
        const messageData = {
          senderAccount: conversation.senderAccount,
          receiverAccount: conversation.receiverAccount || conversation.userAccount,
          msg: content,
          msgType: type
        }

        // 生成消息ID
        const messageId = Date.now().toString()

        // 先在本地添加消息
        const message = {
          id: messageId,
          orgId: messageId,
          content,
          type,
          sender: conversation.senderAccount,
          receiver: conversation.receiverAccount || conversation.userAccount,
          timestamp: new Date().toISOString(),
          isOwn: true,
          status: 'sending'
        }

        commit('ADD_MESSAGE', {
          conversationId: state.activeConversationId,
          message
        })

        // 发送到服务器
        const response = await Vue.prototype.$http.post('/chatMsg/send', messageData)
        if (response.data && response.data.code === 200) {
          // 更新消息状态为已发送
          message.status = 'sent'
          return response.data
        } else {
          message.status = 'failed'
          throw new Error(response.data.msg || '发送失败')
        }
      } catch (error) {
        console.error('Failed to send message:', error)
        throw error
      }
    }
  },

  getters: {
    // 获取活动对话
    activeConversation: (state) => {
      if (!state.activeConversationId) return null

      // 解析对话ID: conv_{staffId}_{sessionId}
      const [staffId, sessionId] = state.activeConversationId.replace('conv_', '').split('_')

      // 通过staffId找到客服，然后在sessions中通过ownerId找到会话
      const staff = state.conversations.find(s => s.staffId == staffId)
      const session = staff && staff.sessions && staff.sessions.find(s => s.customerId == sessionId)

      if (!session) return null

      // console.log('会话信息:', session)
      // 返回会话对象，添加必要字段
      return {
        ...session,
        customId: state.activeConversationId,
        senderAccount: staffId,
        receiverAccount: session.peerId,
        name: ` 客户${session.customerId}`
      }
    },

    // 获取对话消息
    conversationMessages: (state) => (conversationId) => {
      console.log(conversationId, 'conversationId', state.messages)
      return state.messages[conversationId] || []
    },

    // 获取对话未读消息数
    conversationUnreadCount: (state) => (conversationId) => {
      return state.unreadCounts[conversationId] || 0
    },

    // 获取总未读消息数
    totalUnreadCount: (state) => {
      return Object.values(state.unreadCounts).reduce((total, count) => total + count, 0)
    },

    // 是否已登录
    isAuthenticated: (state) => {
      return !!state.authToken
    }
  }
})
